import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, Row, Col, Button, Container, CardHeader } from 'reactstrap';
import BreadcrumbTrail from '../../../../../../Common/BreadcrumbTrail';
import { useHistory } from 'react-router-dom';
import tick from '../../../../../../../assets/images/tick-v2.svg';
import edit from '../../../../../../../assets/images/edit.svg';
import totalAirport from '../../../../../../../assets/custom/v2/total-airports.svg';
import totalZones from '../../../../../../../assets/custom/v2/total-zones.svg';
import totalClients from '../../../../../../../assets/custom/v2/total-clients.svg';
import totalVendors from '../../../../../../../assets/custom/v2/total-vendors.svg';
import ConfirmApproval from '../../../ProgramOnboarding/Components/Comman/Modal/ConfirmApproval';
import { ChevronLeft } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { SAVE_DRAFT_CONFIGURATION, UPDATE_DRAFT_CONFIGURATION } from '../../../../../../../store/actions';
import queryString from 'query-string';

const ConfigurationChecklist = ({ state, setFormCount, configuration, isDraftConfiguration, fetchLiveConfigData }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [isShowApprovalModal, setIsShowApprovalModal] = useState(false);

  const { saveDraftConfiguration, updateDraftConfiguration } = useSelector((state: any) => ({
    saveDraftConfiguration: state.SaveDraftConfiguration.data,

    updateDraftConfiguration: state.UpdateDraftConfiguration.data,
  }));

  useEffect(() => {
    if (saveDraftConfiguration !== null || updateDraftConfiguration !== null) {
      const params = {
        name: 'vendor_onboarding_airport_transfer_saved_configuration_card',
        type: 'saved_configuration_card',
        page_name: 'Saved Configuration',
      };
      history.push({
        pathname: '/v2/approvals-listing',
        search: `?${queryString.stringify(params)}`,
      });
    }
  }, [saveDraftConfiguration, updateDraftConfiguration]);

  const getAlltheAirports = useMemo(() => {
    return state?.vendorPricing?.airports?.map(airport => airport?.code).join(',') || '';
  }, [state?.vendorPricing?.airports]);

  const getAllTheZonesCount = useMemo(() => {
    const seenZoneIds = new Set();

    const countUniqueZones = (airports = []) => {
      for (const airport of airports) {
        for (const zone of airport?.zones || []) {
          if (zone?.id) {
            seenZoneIds.add(zone.id);
          }
        }
      }
    };

    countUniqueZones(state?.vendorPricing?.airports);

    return seenZoneIds.size;
  }, [state?.vendorPricing?.airports]);

  const steps = useMemo(
    () => [
      {
        title: 'Airport Added',
        desc: `${getAlltheAirports} Airports has been added Configured`,
        handleClick: () => setFormCount(3),
      },
      {
        title: 'Zone Created',
        desc: `${getAllTheZonesCount} Zones have been defined and Mapped`,
        handleClick: () => setFormCount(4),
      },
      {
        title: 'Pricing Set',
        desc: 'All Zone pricing has been configured',
        handleClick: () => setFormCount(6),
      },
    ],
    [setFormCount],
  );

  const uniqueZoneIds = useMemo(() => {
    const allZones = [...(state?.vendorPricing?.airports ?? [])].flatMap(airport => airport.zones || []);
    const ids = new Set(allZones.map(zone => zone?.id));
    return ids;
  }, [state]);

  const summaryItems = useMemo(() => {
    const airports = state?.vendorPricing?.airports?.filter(a => a?.tag !== 'default') || [];

    const zones = Array.from(uniqueZoneIds ?? []).filter(z => z?.tag !== 'default');

    const vendors =
      (state?.vendorPricing?.vendors?.filter(v => v?.tag !== 'default')?.length || 0) +
      (state?.vendorPricing?.pricingDetails?.filter(p => p?.tag !== 'default')?.length || 0);

    return [
      {
        icon: totalAirport,
        label: 'Total Airports',
        value: airports?.length || 0,
      },
      {
        icon: totalZones,
        label: 'Total Zones',
        value: zones?.length || 0,
      },
      {
        icon: totalVendors,
        label: 'Total Vendor Pricing',
        value: vendors || 0,
      },
    ];
  }, [state, uniqueZoneIds]);

  const breadcrumNavigationHandler = title => {
    if (title === `Dashboard`) history.push(`/dashboard`);
  };

  const countTotalApproval =
    summaryItems?.reduce((acc, curr) => {
      acc += curr?.value;
      return acc;
    }, 0) ?? 0;

  const syncMultiProjects = (state: any, fetchLiveConfigData: any) => {
    const stateProjects = state?.multiProjects || [];
    const liveProjects = fetchLiveConfigData?.multiProjects || [];

    // Convert to Map for faster lookup by id
    const stateMap = new Map(stateProjects.map((p: any) => [p.id, p]));
    const liveMap = new Map(liveProjects.map((p: any) => [p.id, p]));

    const updatedProjects: any[] = [];

    // Check live projects (mark "removed" if new in live but not in state)
    liveProjects.forEach((project: any) => {
      if (!stateMap.has(project.id)) {
        updatedProjects.push({ ...project, tag: 'removed' });
      } else {
        updatedProjects.push(stateMap.get(project.id)); // keep existing
      }
    });

    // Check state projects (mark "added" if missing in live)
    stateProjects.forEach((project: any) => {
      if (!liveMap.has(project.id)) {
        updatedProjects.push({ ...project, tag: 'added' });
      }
    });

    return {
      ...state,
      multiProjects: updatedProjects,
    };
  };

  const handleSubmit = () => {
    const syncedState = syncMultiProjects(state, fetchLiveConfigData);

    if (isDraftConfiguration) {
      const payload = {
        data: {
          configuration_data: {
            ...syncedState,
            benefit: {
              name: 'Airport Transfer',
            },
            page: 8,
          },
          id: configuration?.id,
          status: 'PENDING',
          configuration_type: 'vendor_onboarding',
          total_number_of_approvals: countTotalApproval,
        },
      };
      dispatch({
        type: UPDATE_DRAFT_CONFIGURATION,
        payload: payload,
      });
    } else {
      const payload = {
        data: {
          configuration_data: {
            ...syncedState,
            benefit: {
              name: 'Airport Transfer',
            },
            page: 8,
          },
          configuration_type: 'vendor_onboarding',
          status: 'PENDING',
          total_number_of_approvals: countTotalApproval,
        },
      };
      dispatch({
        type: SAVE_DRAFT_CONFIGURATION,
        payload: payload,
      });
    }
  };

  return (
    <div className="page-content">
      {isShowApprovalModal && (
        <ConfirmApproval
          onClose={() => {
            setIsShowApprovalModal(false);
          }}
          handleYes={handleSubmit}
        />
      )}
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center">
          <BreadcrumbTrail
            title={`Review & Send For Approval`}
            navigationby={breadcrumNavigationHandler}
            navigation={['Management', 'Program', 'Onboarding', 'Review & Send For Approval']}
          />
        </div>

        <Card>
          <CardHeader>
            <h5 className="m-0 fw-semibold px-3">Configuration Checklist</h5>
          </CardHeader>
          <CardBody className="mt-2 px-5 pe-5">
            <Row>
              <Col md={7} className="mt-4">
                {steps.map((step, idx) => (
                  <Card className="mb-3 border-0 configuration-card" key={idx}>
                    <CardBody className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-start gap-3 px-4 pe-4">
                        <img src={tick} alt="" />
                        <div>
                          <h6 className="mb-1 fw-medium">{step.title}</h6>
                          <p className="fw-normal fs-10 mb-0">{step.desc}</p>
                        </div>
                      </div>
                      <img src={edit} alt="" className="cursor-pointer" onClick={step.handleClick} />
                    </CardBody>
                  </Card>
                ))}
              </Col>
              <Col md={1}></Col>
              <Col md={4} className="mt-4 ">
                <Card className="configuration-card border-0">
                  <CardBody>
                    <h5 className="fw-medium mb-4">Configuration Summary</h5>
                    {summaryItems.map((item, idx) => (
                      <div className="d-flex gap-3 align-items-start mb-4" key={idx}>
                        <img src={item?.icon} alt="" width={38} />
                        <div>
                          <p className="fw-medium m-0 mb-1">{item.label}</p>
                          <p className="text-muted m-0">{item.value}</p>
                        </div>
                      </div>
                    ))}
                    <div className="d-flex justify-content-between mt-4 bg-light p-2 rounded fw-medium px-3 pe-3" style={{ color: '#405189' }}>
                      <p className="m-0">Total approvals required</p>
                      <p className="m-0">{countTotalApproval}</p>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <div className="d-flex justify-content-between">
          <Button
            onClick={() => setFormCount((prev: number) => prev - 1)}
            color="light"
            className="d-flex align-items-center gap-1 px-3 py-2"
            style={{
              backgroundColor: '#E9EBEC', // light gray
              color: '#404B7C', // navy/dark blue
              fontWeight: 500,
              boxShadow: 'none',
              border: 'none',
            }}
          >
            <ChevronLeft size={18} />
            Back
          </Button>

          <Button className="overlay-disable" style={{ width: '200px' }} color="primary" onClick={() => setIsShowApprovalModal(true)}>
            Send For Approval
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default ConfigurationChecklist;
